import { isList, isRecord } from '@shelflens/api';

import { distinctHeaders, type Table } from './csv.js';

/**
 * A table read from a JSON file.
 *
 * A product export is an array of objects, or an object holding one under
 * `products` or `items`. Every key any object has becomes a column, in the
 * order they are first seen, and each value becomes text the way the CSV
 * reader would have it: a list joined with commas, a number or a boolean
 * written out, and anything nested left empty, since there is no one column
 * it belongs in.
 */

const asText = (value: unknown): string => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value.trim();
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
        return value
            .filter((item) => ['string', 'number', 'boolean'].includes(typeof item))
            .map(String)
            .join(', ');
    }
    return '';
};

const recordsOf = (data: unknown): readonly unknown[] | undefined => {
    if (isList(data)) return data;
    if (isRecord(data)) {
        for (const key of ['products', 'items']) {
            const held = data[key];
            if (isList(held)) return held;
        }
    }
    return undefined;
};

/** A JSON file as a table, or an error saying why it is not one. */
export const parseJsonTable = (text: string): Table => {
    let data: unknown;
    try {
        data = JSON.parse(text);
    } catch {
        throw new Error(
            'This file could not be read as JSON. Check that it was saved as JSON, or export it as CSV instead.'
        );
    }
    const listed = recordsOf(data) ?? [];
    const records = listed
        .map((record, index) => ({ record, label: `item ${index + 1}` }))
        .filter((entry): entry is { record: Record<string, unknown>; label: string } =>
            isRecord(entry.record)
        );
    if (records.length === 0) {
        throw new Error(
            'This JSON file has no list of products. It should be a list, or hold one under \u201cproducts\u201d or \u201citems\u201d.'
        );
    }
    const keys: string[] = [];
    const seen = new Set<string>();
    for (const { record } of records) {
        for (const key of Object.keys(record)) {
            if (!seen.has(key)) {
                seen.add(key);
                keys.push(key);
            }
        }
    }
    return {
        headers: distinctHeaders(keys),
        rows: records.map(({ record }) => keys.map((key) => asText(record[key]))),
        labels: records.map(({ label }) => label),
    };
};
